import { Eye, EyeOff, Users } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { Switch } from '@/components/ui/switch';
import { updateSiteSettings } from '@/data/settingsRepo';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { formatDate } from '@/util/util_format';

/**
 * Which sections of the public site partners can see.
 *
 * Feature flags with a switch on them, rather than a deploy: a section can be
 * withdrawn the moment somebody decides it is not ready, by the person who
 * decided, and put back just as quickly. Turning one off hides its navigation
 * *and* closes its route — see `SiteSettingsType.tourLeadersVisible` — so a
 * bookmarked URL does not walk straight past the switch.
 *
 * Admins always keep their own access, which is what makes this usable: you can
 * look at a hidden section before turning it on for anyone else.
 */
export function SiteSectionsCard() {
  const { user } = useAuth();
  const { settings, loading } = useSiteSettings();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggleTourLeaders(visible: boolean) {
    setSaving(true);
    setError(null);
    try {
      await updateSiteSettings({ tourLeadersVisible: visible }, user?.uid);
      // No local state to update — the subscription reflects the write.
    } catch (e) {
      console.error('Failed to save site settings', e);
      setError('Could not save that. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className='tw:rounded-lg tw:border tw:bg-card tw:p-6'>
      <div className='tw:flex tw:items-center tw:gap-3'>
        {settings.tourLeadersVisible ? (
          <Eye className='tw:size-5 tw:text-primary' />
        ) : (
          <EyeOff className='tw:size-5 tw:text-muted-foreground' />
        )}
        <div>
          <p className='tw:font-medium'>Public site sections</p>
          <p className='tw:text-sm tw:text-muted-foreground'>
            What partners can see. Changes take effect immediately, for everyone.
          </p>
        </div>
      </div>

      <div className='tw:mt-5 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4 tw:rounded-md tw:border tw:px-4 tw:py-3'>
        <div className='tw:flex tw:items-start tw:gap-3'>
          <Users className='tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-muted-foreground' />
          <div>
            <label htmlFor='flag-tour-leaders' className='tw:cursor-pointer tw:font-medium'>
              Tour Leaders
            </label>
            <p className='tw:text-sm tw:text-muted-foreground'>
              {loading
                ? 'Checking…'
                : settings.tourLeadersVisible
                  ? 'Partners can browse the roster at /tour-leaders.'
                  : 'Hidden from partners. You can still open it, and still edit the roster.'}
            </p>
          </div>
        </div>
        <Switch
          id='flag-tour-leaders'
          checked={settings.tourLeadersVisible}
          // Disabled until the real value is in, so the switch cannot be read as
          // "off" while it is really "not yet known" — and cannot be flipped
          // from a default that is about to be replaced by the stored one.
          disabled={loading || saving}
          onCheckedChange={(checked) => void toggleTourLeaders(checked)}
        />
      </div>

      {settings.updatedAt && !loading && (
        <p className='tw:mt-3 tw:text-xs tw:text-muted-foreground'>
          Last changed {formatDate(settings.updatedAt)}.
        </p>
      )}

      {error && <p className='tw:mt-3 tw:text-sm tw:text-ed-red'>{error}</p>}
    </section>
  );
}
